// Word import in the browser (`WordImporter`, SuiteImport/WordImporter.swift, ported step for step): a .docx becomes a new
// project, its structure taken from heading styles (`wordOutline.ts`). Comments become margin comments, web links links
// in the text (or margin comments where one can't go in), pictures attachments, footnotes and endnotes footnotes; the text
// before the first heading and headings named like book matter fill the project's book-matter documents, which this
// makes as the Mac makes them (`StoreEditor.addBookMatter`). What can't be kept is listed in the report, and the report
// checks every word of the file arrived. The project's `source` is `word:<SHA-256 of the file>`, as the Mac's; a .docx
// always makes a new project (there's no re-import merge).
import * as A from '@automerge/automerge';
import type { Block, Inline } from '../contract.ts';
import { sha256Hex } from '../hashing.ts';
import { readFragment, readProject } from '../model/documents.ts';
import { orderedFragmentIDs } from '../model/arrangement.ts';
import { BookMatter } from '../bookMatter.ts';
import { sequence } from '../automerge/fractionalIndex.ts';
import { linkAddress, linking, parse, plainStyle, serialize, wordCount } from '../proseMarkdown.ts';
import { StoreEditor } from '../edit/storeEditor.ts';
import { fragmentDocName, putNode } from '../edit/editProject.ts';
import { str, timestamp, type Writer } from '../edit/typed.ts';
import { AnchorSentinels } from './anchorSentinels.ts';
import { ImportEditor, mimeType, type ImportedAttachment, type ScrivenerImportReport } from './scrivenerImporter.ts';
import { isLegacyWord, readWordDocument, WordImportError, type WordDocument, type WordFiles, type WordParagraph } from './wordDocument.ts';
import { buildWordOutline, sectionOptionID, wordCountOf, type WordNode } from './wordOutline.ts';

export const WORD_SOURCE = 'word';
const MESSAGE = 'Imported from Word';

/** `ImportReport` as the Word import fills it: the Scrivener report's fields, with what was made of the document. */
export interface WordImportReport extends ScrivenerImportReport {
  source: 'Word';
  /** What was made of the document (`WordOutline.summary`). */
  structure: string[];
}

export interface WordImportOptions {
  /** The file's name without its extension: the project's title when the document names none. */
  filename: string;
  /** A title the writer chose (New Project's field): the project's, instead of the one the document gives. */
  title?: string;
  /** The writer's name, for link comments and the checkpoint. */
  author: string;
  device?: string;
  locale?: string;
  now?: Date;
  /** Pins ids and actors (tests). */
  makeID?: () => string;
  newActor?: () => string;
  actor?: string;
}

export interface WordImportResult {
  projectID: string;
  type: 'manuscript';
  docs: Map<string, A.Doc<unknown>>;
  report: WordImportReport;
  /** The pictures, by SHA-256 (the fragments already name them), for the app to upload. */
  attachments: ImportedAttachment[];
}

/** `WordImporter.importDocument`: the .docx's bytes (for its hash, and to refuse a .doc) and its parts, unzipped by the
 *  caller (null for a file that isn't a zip). */
export function importWordDocument(bytes: Uint8Array, files: WordFiles | null, o: WordImportOptions): WordImportResult {
  if (isLegacyWord(bytes)) throw new WordImportError('legacyWordFormat');
  if (!files) throw new WordImportError('notAWordDocument');
  const doc = readWordDocument(files);
  const outline = buildWordOutline(doc, o.filename);
  const chosen = o.title?.trim();
  if (chosen) outline.title = chosen;
  const now = o.now ?? new Date();
  const created = doc.created ?? now;
  const modified = new Date(Math.max((doc.modified ?? now).getTime(), created.getTime()));

  const report: WordImportReport = {
    source: 'Word', structure: outline.summary,
    projectID: '', projectTitle: outline.title, isReimport: false, fragmentsCreated: 0, fragmentsUpdated: 0, fragmentsUnchanged: 0, fragmentsRemoved: 0,
    snapshotsImported: 0, commentsImported: 0, linksPreserved: 0, highlightsPreserved: 0, conflicts: 0, structureConflicts: [], attachmentsStored: 0,
    arrangements: [], totalWords: 0, issues: [...doc.issues, ...outline.issues].map((message) => ({ item: o.filename, message })),
    verification: { itemsChecked: 0, textMismatches: [], structureMismatches: [] },
  };

  const projectDoc = StoreEditor.createProject({ title: outline.title, createdAt: created, locale: o.locale, id: o.makeID?.(), actor: o.actor });
  const docs = new Map<string, A.Doc<unknown>>([['project', projectDoc]]);
  const editor = new ImportEditor(docs, { device: o.device ?? 'web', author: o.author, makeID: o.makeID, newActor: o.newActor, actor: o.actor });
  const project = readProject(projectDoc);
  report.projectID = project.id;
  const manuscript = project.arrangements.find((a) => a.role === 'manuscript')!.id;
  const writer = new FragmentWriter(doc, editor, o.author, modified);

  const nodeWrites: Parameters<typeof putNode>[2][] = [];
  const place = (nodes: WordNode[], parent: string | undefined) => {
    const positions = sequence(nodes.length);
    nodes.forEach((node, k) => {
      const id = editor.at(created, () => editor.createFragment({ kind: 'prose', title: node.title, createdAt: created }));
      writer.write(node.paragraphs, id, [], (w) => {
        if (node.sectionType) w.putIfChanged(['metadata'], 'section-type', str(sectionOptionID(node.sectionType)));
        w.putIfChanged([], 'modifiedAt', timestamp(modified));
      });
      const nodeID = editor.newID();
      nodeWrites.push({ id: nodeID, fragmentID: id, isFolder: node.isFolder, parentID: parent, position: positions[k] });
      report.fragmentsCreated += 1;
      place(node.children, nodeID);
    });
  };
  place(outline.nodes, undefined);
  editor.edit('project', MESSAGE, modified, (w) => {
    w.put([], 'source', str(`${WORD_SOURCE}:${sha256Hex(bytes)}`));
    for (const node of nodeWrites) putNode(w, manuscript, node);
  });

  // Front and back matter: the project's own documents, filled from the file.
  editor.at(modified, () => editor.addBookMatter(modified));
  const filled = new Set<BookMatter>();
  for (const [kind, paragraphs] of outline.matter) {
    if (BookMatter.isGenerated(kind)) {
      writer.skippedWords += paragraphs.reduce((n, p) => n + wordCountOf(p.text), 0);
      if (paragraphs.some((p) => p.text !== '')) report.issues.push({ item: o.filename, message: 'The contents list was left out: compile makes one' });
      continue;
    }
    const id = BookMatter.stableID(kind, project.id);
    if (!editor.has(fragmentDocName(id))) continue;
    // A second section of the same kind (two epigraphs, say) follows the first.
    const previous = filled.has(kind) ? parse(readFragment(editor.document(fragmentDocName(id))).text) : [];
    writer.write(paragraphs, id, previous, () => {});
    filled.add(kind);
  }
  const after = readProject(editor.document('project'));
  report.arrangements = after.arrangements.filter((a) => a.role === 'manuscript' || a.role === 'publishing' || a.role === 'reference')
    .sort((a, b) => a.order - b.order || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)).map((a) => a.name);
  report.commentsImported = writer.comments;
  report.linksPreserved = writer.links;
  report.attachmentsStored = writer.attachmentCount;
  report.issues.push(...writer.issues.messages.map((message) => ({ item: o.filename, message })));
  editor.at(now, () => editor.createCheckpointBy(MESSAGE, o.author, 'imported'));

  const arrangement = after.arrangements.find((a) => a.role === 'manuscript');
  let total = 0;
  if (arrangement) {
    for (const id of orderedFragmentIDs(arrangement)) {
      if (editor.has(fragmentDocName(id))) total += wordCount(readFragment(editor.document(fragmentDocName(id))).text);
    }
  }
  report.totalWords = total;

  // Every word of the file is in a document's text or title (bar a contents list).
  report.verification.itemsChecked = report.fragmentsCreated + filled.size;
  const imported = writer.writtenWords + outline.titleWords;
  const expected = outline.sourceWords - writer.skippedWords;
  if (imported !== expected) {
    report.verification.textMismatches.push(`The file has ${expected.toLocaleString('en-US')} words but ${imported.toLocaleString('en-US')} were imported`);
  }
  return { projectID: project.id, type: 'manuscript', docs, report, attachments: writer.attachments() };
}

/** `IssueCounter`. */
class Issues {
  private readonly counts = new Map<string, number>();
  add(message: string) { this.counts.set(message, (this.counts.get(message) ?? 0) + 1); }
  get messages(): string[] { return [...this.counts].map(([m, n]) => (n > 1 ? `${m} (×${n})` : m)); }
}

/** Writes paragraphs into a fragment: text, footnotes, comments, links and pictures. */
class FragmentWriter {
  comments = 0;
  links = 0;
  attachmentCount = 0;
  writtenWords = 0;
  skippedWords = 0;
  readonly issues = new Issues();
  private readonly usedCommentIDs = new Set<string>();
  private readonly stored = new Map<string, ImportedAttachment>();

  constructor(private readonly doc: WordDocument, private readonly editor: ImportEditor, private readonly author: string, private readonly date: Date) {}

  attachments(): ImportedAttachment[] { return [...this.stored.values()]; }

  write(paragraphs: WordParagraph[], fragmentID: string, previous: Block[], extra: (w: Writer) => void) {
    const { doc, editor } = this;
    const blocks: Block[] = [];
    // Consecutive verse lines form a stanza; an empty paragraph between them ends one.
    let stanzaOpen = false;
    for (const p of paragraphs) {
      if (p.role !== 'verse') stanzaOpen = false;
      if (p.role === 'sceneBreak') blocks.push({ kind: { type: 'sceneBreak' }, inlines: [], alignment: null });
      else if (p.role === 'blockQuote') blocks.push({ kind: { type: 'blockQuote' }, inlines: p.inlines, alignment: null });
      else if (p.role === 'verse') {
        const indent = p.level ?? 0;
        const line: Inline[] = (indent > 0 ? [{ type: 'text', text: '\t'.repeat(indent), style: plainStyle() } as Inline] : []).concat(p.inlines);
        const last = blocks[blocks.length - 1];
        if (stanzaOpen && last && last.kind.type === 'verse') last.inlines.push({ type: 'lineBreak' }, ...line);
        else blocks.push({ kind: { type: 'verse' }, inlines: line, alignment: null });
        stanzaOpen = !p.blankAfter;
      } else {
        if (p.text === '') continue;
        if (p.listItem) blocks.push({ kind: { type: 'listItem', style: p.listItem.style, number: p.listItem.number }, inlines: p.inlines, alignment: null });
        else blocks.push({ kind: { type: 'paragraph' }, inlines: p.inlines, alignment: p.alignment });
      }
      this.writtenWords += wordCountOf(p.text);
    }
    while (blocks[0]?.kind.type === 'sceneBreak') blocks.shift();
    while (blocks[blocks.length - 1]?.kind.type === 'sceneBreak') blocks.pop();

    const notes: string[] = [];
    for (const block of blocks) {
      for (const inline of block.inlines) if (inline.type === 'footnoteReference' && !notes.includes(inline.id)) notes.push(inline.id);
    }
    for (const id of notes) blocks.push({ kind: { type: 'footnote', id }, inlines: doc.footnotes.get(id) ?? [], alignment: null });
    const isNote = (b: Block) => b.kind.type === 'footnote';
    const body = [...previous.filter((b) => !isNote(b)), ...blocks, ...previous.filter(isNote)];
    const { text: marked, anchors: found } = AnchorSentinels.extract(serialize(body), true);
    // Web links go into the text as links; one that can't (across a line, inside a span) stays a margin comment.
    const linkAnchors: { index: number; range: [number, number] }[] = [];
    const candidates: { range: [number, number]; address: string }[] = [];
    for (const anchor of found) {
      const kind = doc.anchors[anchor.index];
      if (kind?.type !== 'link') continue;
      linkAnchors.push(anchor);
      candidates.push({ range: anchor.range, address: linkAddress(kind.url) });
    }
    const linked = linking(marked, candidates);
    const inText = new Set(linkAnchors.filter((_, k) => linked.applied[k]).map((a) => a.index));
    const anchors = found.filter((a) => !inText.has(a.index)).map((a) => ({ index: a.index, range: linked.range(a.range) }));
    this.links += inText.size;

    // Pictures: attachments of the document.
    const name = fragmentDocName(fragmentID);
    const refs = [...readFragment(editor.document(name)).attachments.map((a) => a.sha256)];
    const added: { sha256: string; filename: string; mimeType: string }[] = [];
    for (const path of paragraphs.flatMap((p) => p.images)) {
      const bytes = doc.media.get(path);
      if (!bytes) {
        this.issues.add(`A picture (${path}) wasn't in the file`);
        continue;
      }
      const sha256 = sha256Hex(bytes);
      const filename = path.split('/').pop()!;
      if (!this.stored.has(sha256)) this.stored.set(sha256, { sha256, filename, mimeType: mimeType(filename), bytes });
      if (!refs.includes(sha256)) {
        refs.push(sha256);
        added.push({ sha256, filename, mimeType: mimeType(filename) });
        this.attachmentCount += 1;
      }
    }
    if (refs.length) this.issues.add('Pictures moved to the document\'s attachments');

    editor.edit(name, MESSAGE, this.date, (w, _r, d) => {
      A.updateText(d as A.Doc<unknown>, ['text'], linked.text);
      if (added.length) {
        if (!d.attachments) d.attachments = [];
        for (const a of added) {
          (d.attachments as unknown[]).push({ sha256: new A.ImmutableString(a.sha256), filename: new A.ImmutableString(a.filename), mimeType: new A.ImmutableString(a.mimeType) });
        }
      }
      extra(w);
    });

    for (const anchor of anchors) {
      const kind = doc.anchors[anchor.index];
      if (!kind) continue;
      if (kind.type === 'comment') {
        const comment = doc.comments.get(kind.id);
        if (!comment) continue;
        let id = `word-comment-${kind.id}`;
        while (this.usedCommentIDs.has(id)) id += '-2';
        this.usedCommentIDs.add(id);
        editor.at(comment.date ?? this.date, () => editor.addMarginComment(fragmentID, {
          range: anchor.range, body: comment.body, author: comment.author, authorKind: 'human', id, message: MESSAGE,
        }));
        this.comments += 1;
      } else {
        editor.at(this.date, () => editor.addMarginComment(fragmentID, {
          range: anchor.range, body: `Link: ${kind.url}`, author: this.author, authorKind: 'human', id: `word-link-${anchor.index}`, message: MESSAGE,
        }));
        this.links += 1;
      }
    }
  }
}
